import { useMemo, useState } from 'react'
import { Icon, Pagination, SlideOver } from '@bloo-team/ui'
import { Copy, Download, PanelLeft } from '@bloo-team/ui/icons'
import {
  addDqlCondition, bareField, fieldNamed, formatCell, isLogEventField, parseLogEvent, populatedFields,
} from '@bloo-team/api-client'
import type { SearchResultPage, SearchRow, WorkbookBlock } from '@bloo-team/api-client'
import {
  AggBar, AggDonut, AggTreemap, ColumnsMenu, downloadText, EXPORT_MAX_ROWS, exportFilename, FieldSidebar, isDemoSession,
  RawEventView, ResultsTable, toAggData, toCsv, toJsonExport,
} from '@bloo-team/core-shell'
import type { EntityKind } from '@bloo-team/core-shell'
import { aggFieldsOf } from '../../lib/workbook-blocks'
import { blockRuntimeFor, exportBlockResult, useBlockResult } from '../../lib/workbook-run'
import type { BlockRun } from '../../lib/workbook-run'

/** Search's rule: populated columns up to 40; the rest are in the row detail. */
const MAX_COLUMNS = 40

/**
 * Charts for a two-column groupby result — the fabric.search agg charts, with
 * the pivot handed to the full Search workspace (the query gains the picked
 * condition and opens there).
 */
export function AggCharts({
  fields, rows, query, onPivot,
}: {
  fields: string[]
  rows: SearchRow[]
  query: string
  onPivot: (q: string) => void
}) {
  const agg = aggFieldsOf(fields)
  const [kind, setKind] = useState<'bar' | 'donut' | 'treemap'>(() => {
    try {
      const v = window.localStorage.getItem('bloo.hunts.aggchart')
      return v === 'donut' || v === 'treemap' ? v : 'bar'
    } catch {
      return 'bar'
    }
  })
  const data = useMemo(
    () => (agg ? toAggData(rows, agg.value, agg.count) : []),
    [rows, agg?.value, agg?.count], // eslint-disable-line react-hooks/exhaustive-deps
  )
  if (!agg || data.length === 0) return null
  const choose = (v: 'bar' | 'donut' | 'treemap') => {
    setKind(v)
    try {
      window.localStorage.setItem('bloo.hunts.aggchart', v)
    } catch {
      // Preference only.
    }
  }
  const pick = (value: string) => {
    const next = query ? addDqlCondition(query, { field: bareField(agg.value), op: '=', value }) : null
    if (next) onPivot(next)
  }
  return (
    <div className="hud-stack wb-gap-1">
      <div className="hud-row wb-gap-1">
        <span className="hud-eyebrow">chart</span>
        {(['bar', 'donut', 'treemap'] as const).map((v) => (
          <button key={v} type="button" className="list-chip" aria-pressed={kind === v} onClick={() => choose(v)}>
            {v}
          </button>
        ))}
      </div>
      {kind === 'bar' ? (
        <AggBar data={data} field={agg.value} onPick={pick} />
      ) : kind === 'donut' ? (
        <AggDonut data={data} field={agg.value} onPick={pick} />
      ) : (
        <AggTreemap data={data} field={agg.value} onPick={pick} />
      )}
    </div>
  )
}

/**
 * The result area under a block: run state, then the block's own result page.
 * A block whose task id is inherited (Text) shows no rows of its own.
 */
export function BlockResultArea({
  clusterId, scopeId, block, index, run, onPivot, onEntity,
}: {
  clusterId: string
  scopeId: string
  block: WorkbookBlock
  index: number
  run: BlockRun | undefined
  /** Open Search on a query (a pivot, or the block's own query). */
  onPivot: (q: string) => void
  /** Open Search's entity mode on a cell value. */
  onEntity: (kind: EntityKind, value: string) => void
}) {
  const [pageNo, setPageNo] = useState(1)
  const [pageSize, setPageSize] = useState(50)
  const ready = run?.status === 'done' && run.ownJob ? run.taskId : null
  const result = useBlockResult({ clusterId, scopeId, taskId: ready, pageNo, pageSize })

  if (!run) {
    return <p className="wb-result hud-unit mono" data-st="idle">not run · ▶ to run</p>
  }
  if (run.status === 'running') {
    return <p className="wb-result hud-unit mono" data-st="running" role="status">running…</p>
  }
  if (run.status === 'failed') {
    return (
      <div className="bui-alert bui-alert-error" role="alert">
        <span className="bui-alert-mark">✕</span>
        <span>{run.error ?? 'The block failed.'}</span>
      </div>
    )
  }
  if (!run.ownJob || !ready) {
    return (
      <p className="wb-result hud-unit mono" data-st="done">
        {run.taskId ? 'passes the previous block’s result on' : 'nothing above to pass on'}
      </p>
    )
  }
  if (result.isPending) return <p className="wb-result hud-unit mono" role="status">loading result…</p>
  if (result.isError || !result.data) return <p className="wb-result hud-unit mono">The result could not be loaded.</p>
  const page = result.data
  if (page.failed) {
    return (
      <div className="bui-alert bui-alert-error" role="alert">
        <span className="bui-alert-mark">✕</span>
        <span>{page.message ?? 'The block failed to execute.'}</span>
      </div>
    )
  }
  return (
    <ResultBand
      // Keyed by task: a new run starts with fresh columns, hidden set and panels.
      key={ready}
      clusterId={clusterId}
      scopeId={scopeId}
      taskId={ready}
      index={index}
      block={block}
      page={page}
      pageNo={pageNo}
      pageSize={pageSize}
      busy={result.isFetching}
      onPageChange={setPageNo}
      onPageSizeChange={(n) => {
        setPageSize(n)
        setPageNo(1)
      }}
      onPivot={onPivot}
      onEntity={onEntity}
    />
  )
}

/**
 * Tier 3: the result — a band across the card's foot, laid out like the
 * Search page's result panel from the same core-shell pieces: pager top and
 * bottom (fields, columns and export on the top row), the groupby chart, the
 * fields sidebar beside the results table, the row detail and the raw view.
 * No timeline: that is a second count job per block.
 */
function ResultBand({
  clusterId, scopeId, taskId, index, block, page, pageNo, pageSize, busy, onPageChange, onPageSizeChange, onPivot, onEntity,
}: {
  clusterId: string
  scopeId: string
  taskId: string
  index: number
  block: WorkbookBlock
  page: SearchResultPage
  pageNo: number
  pageSize: number
  busy: boolean
  onPageChange: (n: number) => void
  onPageSizeChange: (n: number) => void
  onPivot: (q: string) => void
  onEntity: (kind: EntityKind, value: string) => void
}) {
  const n = index + 1
  const query = block.type === 'dql' || block.type === 'sql' ? block.query : ''
  const dql = block.type === 'dql' && query !== ''
  const rows = page.rows
  // Frozen from the first page of this task, like Search: paging never reflows the table.
  const [allColumns] = useState(() => populatedFields(page.fields, page.rows))
  const [hidden, setHidden] = useState<Set<string>>(() => new Set())
  const [fieldsOpen, setFieldsOpen] = useState(false)
  const [openRow, setOpenRow] = useState<SearchRow | null>(null)
  const [rawView, setRawView] = useState<{ field: string; text: string } | null>(null)
  const [exporting, setExporting] = useState<'csv' | 'json' | null>(null)
  const [exportNote, setExportNote] = useState<string | null>(null)

  const shown = allColumns.filter((c) => !hidden.has(c))
  const columns = shown.slice(0, MAX_COLUMNS)
  const hiddenColumnCount = Math.max(0, shown.length - columns.length)
  const toggleColumn = (c: string) =>
    setHidden((h) => {
      const next = new Set(h)
      if (next.has(c)) next.delete(c)
      else next.add(c)
      return next
    })

  /** The sidebar's fields: populated, real (no synthetics, no raw line). */
  const sidebarFields = useMemo(() => allColumns.filter((f) => !/_col\d+$/i.test(f) && !isLogEventField(f)), [allColumns])
  /** Top values per field, counted over the rows on screen — no queries. */
  const localFacets = useMemo(() => {
    const out = new Map<string, Array<[string, number]>>()
    for (const f of sidebarFields) {
      const counts = new Map<string, number>()
      for (const r of rows) {
        const v = r[f]
        if (v === null || v === undefined || v === '') continue
        counts.set(String(v), (counts.get(String(v)) ?? 0) + 1)
      }
      out.set(f, [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 5))
    }
    return out
  }, [sidebarFields, rows])

  /** Search's pivot rule: DQL only; no booleans, raw lines or synthetic aggregate columns. */
  const canPivot = (field: string, value: unknown) =>
    dql &&
    value !== null &&
    value !== undefined &&
    value !== '' &&
    typeof value !== 'boolean' &&
    !isLogEventField(field) &&
    !/_col\d+$/i.test(field)
  const pivot = (field: string, value: unknown, op: '=' | '!=') => {
    const next = addDqlCondition(query, { field: bareField(field), op, value: String(value) })
    if (next) onPivot(next)
  }

  const doExport = async (kind: 'csv' | 'json') => {
    setExporting(kind)
    setExportNote(null)
    try {
      const all = await exportBlockResult(blockRuntimeFor(isDemoSession()), { clusterId, scopeId, taskId })
      if (!all) {
        setExportNote('export failed — the stored result could not be re-read')
        return
      }
      // Populated columns over EVERY exported row, as Search does.
      const cols = populatedFields(all.fields, all.rows)
      const body = kind === 'csv' ? toCsv(cols, all.rows) : toJsonExport(cols, all.rows)
      downloadText(exportFilename(kind), kind === 'csv' ? 'text/csv' : 'application/json', body)
      if (all.truncated) setExportNote(`export capped at ${EXPORT_MAX_ROWS.toLocaleString('en-US')} rows`)
    } catch {
      setExportNote('export failed — the stored result could not be re-read')
    } finally {
      setExporting(null)
    }
  }

  const streamField = fieldNamed(page.fields, 'Stream') ?? fieldNamed(page.fields, 'SourceType')
  const timeField = fieldNamed(page.fields, 'CNAMTime')

  return (
    <div className="wb-result-band" role="region" aria-label={`Block ${n} result`}>
      <Pagination
        label={`Block ${n} results, top`}
        page={pageNo}
        pageSize={pageSize}
        total={page.total}
        busy={busy}
        unit="rows"
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      >
        {rows.length ? (
          <span className="q-export">
            <button type="button" className="pager-btn" aria-pressed={fieldsOpen} onClick={() => setFieldsOpen((o) => !o)}>
              <Icon icon={PanelLeft} size={11} /> fields
            </button>
            <ColumnsMenu columns={allColumns} hidden={hidden} onToggle={toggleColumn} />
            {(['csv', 'json'] as const).map((kind) => (
              <button key={kind} type="button" className="pager-btn" disabled={exporting !== null} onClick={() => void doExport(kind)}>
                <Icon icon={Download} size={11} /> {exporting === kind ? 'exporting…' : kind}
              </button>
            ))}
            {exportNote ? <span className="q-cost" role="status">{exportNote}</span> : null}
          </span>
        ) : null}
      </Pagination>
      {rows.length === 0 ? (
        <p className="hud-unit" role="status">{page.message ?? 'The block returned no rows.'}</p>
      ) : (
        <>
          <AggCharts fields={page.fields} rows={rows} query={dql ? query : ''} onPivot={onPivot} />
          <div className="q-body">
            {fieldsOpen ? (
              <FieldSidebar
                fields={sidebarFields}
                facets={localFacets}
                serverField={null}
                server={{ counting: false, failed: false, rows: [], valueField: null, countField: null }}
                pivotEnabled={dql}
                exactAvailable={false}
                onPivotValue={(field, value) => pivot(field, value, '=')}
                onCountExact={() => {}}
                onClose={() => setFieldsOpen(false)}
              />
            ) : null}
            <div className="q-main">
              <ResultsTable
                columns={columns}
                rows={rows}
                openRow={openRow}
                onOpenRow={setOpenRow}
                onRawView={(field, text) => setRawView({ field, text })}
                canPivot={canPivot}
                onPivot={pivot}
                onEntity={onEntity}
              />
              {hiddenColumnCount > 0 ? (
                <p className="hud-unit">
                  {hiddenColumnCount} further populated {hiddenColumnCount === 1 ? 'column is' : 'columns are'} in the row detail.
                </p>
              ) : null}
              <Pagination
                label={`Block ${n} results, bottom`}
                page={pageNo}
                pageSize={pageSize}
                total={page.total}
                busy={busy}
                unit="rows"
                onPageChange={onPageChange}
              />
            </div>
          </div>
        </>
      )}
      <p className="wb-result-foot">
        {page.total} {page.total === 1 ? 'row' : 'rows'}
        {page.executionTime ? ` · ${page.executionTime}` : ''}
        {query ? (
          <>
            {' · full workspace: '}
            <button type="button" className="wb-link" onClick={() => onPivot(query)}>
              open in Search
            </button>
          </>
        ) : null}
      </p>

      {/* Every field of the row, the empty ones included, as on Search. */}
      <SlideOver
        open={openRow !== null}
        onClose={() => setOpenRow(null)}
        eyebrow={openRow ? `row${streamField ? ` · ${formatCell(streamField, openRow[streamField])}` : ''}` : undefined}
        title={openRow && timeField ? formatCell(timeField, openRow[timeField]) : 'Row detail'}
      >
        {openRow ? (
          <dl className="kv">
            {page.fields.map((f) => (
              <div key={f} className="wb-contents">
                <dt>{f}</dt>
                <dd className="mono">
                  {isLogEventField(f) && typeof openRow[f] === 'string' && openRow[f] ? (
                    <pre className="raw-log">{openRow[f]}</pre>
                  ) : (
                    formatCell(f, openRow[f])
                  )}
                </dd>
              </div>
            ))}
          </dl>
        ) : null}
      </SlideOver>

      {/* The raw event, on demand. Copy is the most common next action. */}
      <SlideOver
        open={rawView !== null}
        onClose={() => setRawView(null)}
        eyebrow={rawView ? `raw · ${rawView.field}` : undefined}
        title="Log event"
        actions={rawView ? <RawCopyActions text={rawView.text} /> : null}
      >
        {rawView ? <RawEventView text={rawView.text} /> : null}
      </SlideOver>
    </div>
  )
}

function RawCopyActions({ text }: { text: string }) {
  const p = parseLogEvent(text)
  const json = p.kind === 'text' ? null : p.kind === 'json' ? p.pretty : JSON.stringify(Object.fromEntries(p.pairs), null, 2)
  return (
    <>
      <button type="button" className="bui-btn bui-btn-outline bui-btn-sm" onClick={() => void navigator.clipboard.writeText(text)}>
        <Icon icon={Copy} size={13} /> Copy raw
      </button>
      {json ? (
        <button type="button" className="bui-btn bui-btn-ghost bui-btn-sm" onClick={() => void navigator.clipboard.writeText(json)}>
          <Icon icon={Copy} size={13} /> Copy as JSON
        </button>
      ) : null}
    </>
  )
}
